import { watch } from "vue";
import { useForm } from "#ce/composables/useForm";
import { useHostElement } from "#ce/composables/useHostElement";

/**
 * The omnisearch's form participation: a free-typed query submits as the
 * host's own form value; a picked suggestion instead clears that value and
 * stages its filters as hidden light-DOM inputs (so they submit as their own
 * params); a suggestion carrying a URL navigates. Free text and a picked
 * item are mutually exclusive.
 *
 * @param {Object} options
 * @param {import('vue').Ref<boolean>} options.disabled - mirrored onto the staged filter inputs
 */
export function useEventsOmnisearchForm({ disabled }) {
  const { setValue, emitChange, onFormReset } = useForm();
  const host = useHostElement();

  function slottedFilterInputs() {
    return host ? [...host.querySelectorAll('input[slot="filters"]')] : [];
  }

  function clearFilters() {
    slottedFilterInputs().forEach((input) => input.remove());
  }

  function slotFilters(filters) {
    clearFilters();
    if (!host) {
      return;
    }
    for (const [name, value] of Object.entries(filters)) {
      const input = document.createElement("input");
      input.setAttribute("slot", "filters");
      input.setAttribute("type", "hidden");
      input.setAttribute("name", name);
      input.setAttribute("value", value);
      if (disabled.value) {
        input.setAttribute("disabled", "");
      }
      host.append(input);
    }
  }

  function commitFreeText(text) {
    clearFilters();
    setValue(text);
  }

  function commitFilters(filters) {
    setValue(null);
    slotFilters(filters ?? {});
  }

  // Drops everything the element has committed — the form value and the staged
  // filter inputs — without dispatching anything (a reset is silent, unlike the
  // commits above whose `setValue` reports a bare `change`).
  function clearCommits() {
    setValue(null, { emit: false });
    clearFilters();
  }

  /** Applied contribution state participates in a shared finder form silently. */
  function mirrorQuery(text) {
    setValue(text || null, { emit: false });
  }

  // A synthetic light-DOM anchor click, so page-level click handlers can
  // still intercept the navigation. Only http(s) destinations are clicked —
  // a payload can never inject a script-executing scheme.
  function navigate(url) {
    if (!host) {
      return;
    }
    let protocol;
    try {
      protocol = new URL(url, window.location.href).protocol;
    } catch {
      return;
    }
    if (protocol !== "http:" && protocol !== "https:") {
      return;
    }
    const a = document.createElement("a");
    a.href = url;
    host.appendChild(a);
    a.click();
    a.remove();
  }

  watch(disabled, (value) => {
    slottedFilterInputs().forEach((input) => input.toggleAttribute("disabled", Boolean(value)));
  });

  return { commitFreeText, commitFilters, clearCommits, mirrorQuery, emitChange, navigate, onFormReset };
}
